import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { bool, func, number, object, string } from 'prop-types';

import { fetchConfigurationIfNeeded } from '@lootboy/lib/actions/ConfigurationActions';
import { fetchCountryIfNeeded } from '@lootboy/lib/actions/CountryActions';
import {
  FALLBACK_STRIPE_IMAGE_ID,
  fetchSkrillMethods,
  getInitialPurchaseTypes,
  MASTERCARD,
  SKRILL_COUNTRIES,
  VISA,
  VISA_MASTERCARD,
  VISA_MASTERCARD_AMEX,
} from '@lootboy/lib/payments';
import { configurationSelector } from '@lootboy/lib/selectors';
import { NOOP } from '@lootboy/lib/utils';

import ActivityIndicator from '~/components/ActivityIndicator';
import { SIGNUM_TYPE } from '~/components/LootPackViews/shared';

import {
  authSelector,
  countrySelector,
  geoCountrySelector,
} from '~/redux/selectors';
import { lastPaymentMethodSelector } from '~/redux/selectors/payments';

import empty from '~/assets/images/payments/empty.svg';
import mastercard from '~/assets/images/payments/mastercard.svg';
import paysafecard from '~/assets/images/payments/paysafecard.svg';
// eslint-disable-next-line camelcase
import paysafecard_us from '~/assets/images/payments/paysafecard_us.png';
import signumDe from '~/assets/images/payments/signum-de.png';
import signumEn from '~/assets/images/payments/signum-en.png';
import signumEs from '~/assets/images/payments/signum-es.png';
import signumFr from '~/assets/images/payments/signum-fr.png';
import signumPt from '~/assets/images/payments/signum-pt.png';
import skrill from '~/assets/images/payments/skrill.svg';
import sofortueberweisung from '~/assets/images/payments/sofort.svg';
import lootTicket from '~/assets/images/payments/tickets.png';
import visa from '~/assets/images/payments/visa.svg';
import visaMasterCard from '~/assets/images/payments/visa_mastercard.png';
import visaMasterCardAmex from '~/assets/images/payments/visa_mastercard_amex.png';

import PaymentMethodButton from './PaymentMethodButton';

import style from './PaymentMethodSelector.module.scss';

const stipeImages = {
  [VISA]: visa,
  [VISA_MASTERCARD]: visaMasterCard,
  [VISA_MASTERCARD_AMEX]: visaMasterCardAmex,
  [MASTERCARD]: mastercard,
};

const images = {
  mastercard,
  visa,
  paysafecard,
  paysafecard_us,
  sofortueberweisung,
  lootTicket,
  skrill,
  signum: {
    en: signumEn,
    de: signumDe,
    fr: signumFr,
    es: signumEs,
    pt: signumPt,
  },
};

const SIGNUM_METHOD = {
  type: SIGNUM_TYPE,
  icon: images.signum,
};

// todo this should be moved to redux
async function getPurchaseTypes({
  country,
  acceptTicketPayment,
  isStripeMethodEnabled,
  isSignumMethodEnabled,
  currency,
  ipCountry,
  stripeImageId,
  apiUrl,
}) {
  const skrillMethods = SKRILL_COUNTRIES.includes(ipCountry)
    ? fetchSkrillMethods(apiUrl, ipCountry).then(
        (result) =>
          result.map((method) => ({
            ...method,
            icon: empty,
          })),
        () => [],
      )
    : [];
  const signumMethods = isSignumMethodEnabled ? [SIGNUM_METHOD] : [];
  const availablePurchaseTypes = Object.values(
    getInitialPurchaseTypes({
      platform: 'web',
      isStripeMethodEnabled,
      currency,
      ipCountry,
      country,
      acceptTicket: acceptTicketPayment,
    }),
  ).map((purchaseType) => {
    const { icon, ...method } = purchaseType;
    if (method.type === 'stripecc') {
      method.icon =
        stipeImages[stripeImageId] || stipeImages[FALLBACK_STRIPE_IMAGE_ID];
    } else {
      method.icon = images[icon];
    }
    return method;
  });
  return availablePurchaseTypes.concat(await skrillMethods, signumMethods);
}

const INITIALIZED = 'I';
const LOADING = 'L';
const READY = 'R';

const PaymentMethodSelector = ({
  currency,
  methodSelected,
  onMethodSelected,
  acceptTicketPayment,
  quantity,
}) => {
  const { search } = useLocation();
  const dispatch = useDispatch();
  const geoCountry = useSelector(geoCountrySelector);
  const country = useSelector(countrySelector);
  const { apiUrl } = useSelector(authSelector);
  const lastPaymentMethod = useSelector(lastPaymentMethodSelector);
  const {
    stripeImageId,
    isStripeMethodEnabled,
    isSignumMethodEnabled = search === '?d=FxAW0eAIzozBAioBQLCekT8kfxdRvX',
  } = useSelector(configurationSelector);

  const [availablePurchaseTypes, setAvailablePurchaseTypes] = useState([]);
  const [state, setState] = useState(INITIALIZED);

  useEffect(() => {
    if (
      state === READY &&
      (!methodSelected ||
        (methodSelected.minimumQuantity &&
          methodSelected.minimumQuantity > quantity))
    ) {
      const methods = availablePurchaseTypes.filter(
        ({ minimumQuantity }) =>
          !minimumQuantity || minimumQuantity <= quantity,
      );
      const lastMethod =
        lastPaymentMethod &&
        methods.find(
          ({ type, paymentMethod }) =>
            lastPaymentMethod.type === type &&
            lastPaymentMethod.paymentMethod === paymentMethod,
        );

      onMethodSelected(lastMethod || methods[0]);
    }
  }, [
    lastPaymentMethod,
    availablePurchaseTypes,
    methodSelected,
    onMethodSelected,
    quantity,
    state,
  ]);

  useEffect(() => {
    if (state === INITIALIZED) {
      (async () => {
        setState(LOADING);
        const data = await dispatch(fetchConfigurationIfNeeded()).catch(NOOP);
        const ipCountry = await dispatch(fetchCountryIfNeeded()).catch(
          // eslint-disable-next-line max-nested-callbacks
          () => geoCountry,
        );
        setAvailablePurchaseTypes(
          await getPurchaseTypes({
            country,
            acceptTicketPayment,
            isStripeMethodEnabled,
            isSignumMethodEnabled: data
              ? data.isSignumMethodEnabled
              : isSignumMethodEnabled,
            currency,
            ipCountry,
            stripeImageId,
            apiUrl,
          }),
        );
      })()
        .catch(NOOP)
        .finally(() => setState(READY));
    }
  }, [
    acceptTicketPayment,
    apiUrl,
    country,
    currency,
    dispatch,
    geoCountry,
    isSignumMethodEnabled,
    isStripeMethodEnabled,
    state,
    stripeImageId,
  ]);

  if (state !== READY) {
    return (
      <div>
        <ActivityIndicator />
      </div>
    );
  }
  return (
    <div className={style.paymentMethods}>
      {availablePurchaseTypes.map((purchaseType, ndx) => {
        const shouldRender =
          !purchaseType.minimumQuantity ||
          purchaseType.minimumQuantity <= quantity;
        return (
          shouldRender && (
            <PaymentMethodButton
              key={`${purchaseType.type}_${ndx}`}
              onClick={onMethodSelected}
              selected={methodSelected === purchaseType}
              data={purchaseType}
            />
          )
        );
      })}
    </div>
  );
};

PaymentMethodSelector.propTypes = {
  methodSelected: object,
  onMethodSelected: func,
  acceptTicketPayment: bool,
  currency: string,
  quantity: number,
};

export default PaymentMethodSelector;
